// CuestionarioModal — encuesta de feedback que se ofrece a usuarios de planes
// pagos. Al completarla, el usuario recibe +10 imágenes y la columna
// `cuestionario_respondido` de `company_profiles` queda en true para que el
// banner que dispara este modal no vuelva a aparecer. La lógica de visibilidad y
// el banner viven en `HomeBannersView` (consumidor, compartido por la interfaz
// clásica y V2); este archivo solo implementa el flujo intro → form → submit.
//
// Las preguntas cambiaron dos veces:
//  • 2026-07-29 (ADR-682): las dos primeras de la v1 ("¿Cómo describirías a
//    Atlas?" / "¿Para qué usás Atlas?") se reemplazaron por tres de perfil
//    comercial (tienda online, publicidad, rol). Las columnas viejas
//    `descripcion_atlas` y `usos_atlas` NO se reutilizan: siguen en la tabla con
//    las 144 respuestas de la v1 y ya no se escriben.
//  • 2026-08-06 (ADR-2026-08-06-cuestionario-pregunta-otra-plataforma-ia): se
//    sumó el bloque de competencia (¿probaste otra plataforma de IA? → ¿cuál? →
//    ¿qué hace Atlas mejor?), con las dos últimas CONDICIONADAS a que la
//    respuesta sea "sí". Por eso el wizard tiene 8 pasos o 6, y los pasos se
//    arman en un array en vez de un `switch` con índices fijos.
//
// El modal tiene tres pantallas internas:
//   1. Intro: copy de invitación + botón "Responder" — explica el incentivo
//      antes de mostrar las preguntas para que el usuario decida con
//      contexto si vale la pena dedicar tiempo.
//   2. Form: wizard de una pregunta por paso. Mostrar de a una hace que la
//      encuesta se sienta más liviana y dirigida que un formulario largo
//      donde se ven todas las preguntas a la vez.
//   3. Success: confirmación del premio.

import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Loader2,
  Sparkles,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  FREE_IMAGES_REWARD,
  submitCuestionario,
} from "../lib/cuestionarioReward";
import { PlatformLogoChip } from "./PlatformLogos";

// El envío (guardar respuesta + acreditar el premio + marcar la bandera) vive en
// `lib/cuestionarioReward.ts`. Este componente solo arma las respuestas y muestra
// el resultado: éxito, "ya habías respondido" o error reintentable.

// Escotilla de escape de las dos listas cerradas: al marcarla aparece un campo
// de texto ahí mismo. El detalle viaja en su propia columna
// (`tienda_online_otra` / `publicidad_otra`) para no contaminar con texto libre
// unas listas que son vocabulario controlado.
const OPCION_OTRA = "otra";

// Q1 — sistema de tienda online. Opción ÚNICA: la pregunta apunta a la
// plataforma principal del negocio.
const TIENDA_OPCIONES: Array<{ id: string; label: string }> = [
  { id: "tiendanube", label: "Tiendanube" },
  { id: "empretienda", label: "Empretienda" },
  { id: "mercadolibre", label: "MercadoLibre" },
  { id: OPCION_OTRA, label: "Otra" },
  { id: "ninguna", label: "No tengo tienda online" },
];

// Q2 — dónde paga publicidad. MULTI-select: una empresa suele pagar en varias
// plataformas a la vez y con opción única se perdería esa información.
// `ninguna` es EXCLUYENTE: marcarla destilda el resto y viceversa. "Otra", en
// cambio, es una plataforma más y convive con Instagram/Facebook/Google.
const PUBLICIDAD_OPCIONES: Array<{ id: string; label: string }> = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "google", label: "Google" },
  { id: OPCION_OTRA, label: "Otra" },
  { id: "ninguna", label: "No pagamos por publicidad" },
];

const PUBLICIDAD_NINGUNA = "ninguna";

/** Input del detalle de "Otra": va indentado bajo la opción que lo dispara. */
const InputOtra: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <div className="pl-10">
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus
      className="w-full h-11 px-3 border border-neutral-200 rounded-md text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
    />
  </div>
);

/** Clases del botón-opción full-width, el patrón visual de los pasos de elección. */
const opcionBtn = (selected: boolean) =>
  `w-full flex items-center gap-3 text-left px-3.5 py-2.5 rounded-md text-[15px] transition-colors border ${
    selected
      ? "border-brand-primary bg-brand-primary text-white"
      : "bg-white text-brand-black/85 border-neutral-200 hover:border-brand-primary/40"
  }`;

interface CuestionarioModalProps {
  open: boolean;
  idCompany: string;
  onClose: () => void;
  /**
   * Llamado cuando el envío del cuestionario fue exitoso. Recibe la cantidad
   * de imágenes acreditadas para que el caller actualice su estado local
   * (e.g. badge de imágenes restantes) sin tener que refetchear.
   */
  onSubmitted: (imagesCredited: number) => void;
}

const CuestionarioModal: React.FC<CuestionarioModalProps> = ({
  open,
  idCompany,
  onClose,
  onSubmitted,
}) => {
  // Pantalla actual: intro (copy + CTA) o form (preguntas).
  const [phase, setPhase] = useState<"intro" | "form" | "success">("intro");
  // Índice del paso actual. El TOTAL ya no es una constante: los pasos se arman
  // en cada render y dos de ellos son condicionales (ver `steps`).
  const [step, setStep] = useState(0);
  // Q1 — tienda online (opción única) + el detalle si eligió "Otra".
  const [tiendaOnline, setTiendaOnline] = useState<string | null>(null);
  const [tiendaOnlineOtra, setTiendaOnlineOtra] = useState("");
  // Q2 — publicidad (multi-select, con "ninguna" excluyente) + detalle de "Otra".
  const [publicidadSelected, setPublicidadSelected] = useState<string[]>([]);
  const [publicidadOtra, setPublicidadOtra] = useState("");
  // Q3 — ¿probó otra plataforma de IA? `null` = todavía sin responder.
  const [proboOtraIa, setProboOtraIa] = useState<boolean | null>(null);
  // Q4 / Q5 — solo existen si Q3 es "sí". "Cuál" es obligatoria (es la data
  // dura); el comparativo es opcional para no perder la encuesta entera.
  const [cualOtraIa, setCualOtraIa] = useState("");
  const [atlasMejorQue, setAtlasMejorQue] = useState("");
  // Q6 — rol en la empresa (texto libre, obligatorio).
  const [rolEmpresa, setRolEmpresa] = useState("");
  // Q7 / Q8 — feedback, texto libre opcional.
  const [funcionDeseada, setFuncionDeseada] = useState("");
  const [mejoraSugerida, setMejoraSugerida] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Imágenes REALMENTE acreditadas, según la respuesta del backend. Antes la
  // pantalla de éxito cantaba 10 fijas aunque no se hubiera acreditado nada.
  const [imagesCredited, setImagesCredited] = useState(FREE_IMAGES_REWARD);
  // 409 del backend: la empresa ya había respondido. No se re-acredita, pero el
  // banner tiene que irse igual (la bandera ya está en true del lado del server).
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);

  if (!open) return null;

  // Elegir tienda: cambiar a una opción de la lista borra el detalle de "Otra"
  // para no mandar un texto que ya no corresponde a lo elegido.
  const elegirTienda = (id: string) => {
    setTiendaOnline(id);
    if (id !== OPCION_OTRA) setTiendaOnlineOtra("");
  };

  // Toggle de publicidad con la regla de exclusión: "No pagamos por
  // publicidad" no puede convivir con una plataforma concreta, así que
  // marcarla limpia el resto y marcar una plataforma la limpia a ella.
  // "Otra" es una plataforma más; lo único especial es que al destildarla (o al
  // barrerla "ninguna") hay que limpiar su texto.
  const togglePublicidad = (id: string) => {
    const prev = publicidadSelected;
    let next: string[];
    if (id === PUBLICIDAD_NINGUNA) {
      next = prev.includes(PUBLICIDAD_NINGUNA) ? [] : [PUBLICIDAD_NINGUNA];
    } else {
      const sinNinguna = prev.filter((x) => x !== PUBLICIDAD_NINGUNA);
      next = sinNinguna.includes(id)
        ? sinNinguna.filter((x) => x !== id)
        : [...sinNinguna, id];
    }
    setPublicidadSelected(next);
    if (!next.includes(OPCION_OTRA)) setPublicidadOtra("");
  };

  // Cambiar Q3 a "no" borra las respuestas de la rama "sí": si el usuario
  // escribió, volvió con Atrás y cambió de opinión, esos dos pasos dejan de
  // existir y mandar su texto guardaría la respuesta de una rama descartada.
  const responderProboOtraIa = (valor: boolean) => {
    setProboOtraIa(valor);
    if (!valor) {
      setCualOtraIa("");
      setAtlasMejorQue("");
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await submitCuestionario(idCompany, {
        tiendaOnline: tiendaOnline!, // el paso 0 es obligatorio: acá nunca es null
        tiendaOnlineOtra,
        publicidad: publicidadSelected,
        publicidadOtra,
        proboOtraIa: proboOtraIa!, // el paso "otra-ia" es obligatorio: nunca null
        cualOtraIa,
        atlasMejorQue,
        rolEmpresa,
        funcionDeseada,
        mejoraSugerida,
      });

      // Solo se muestra el éxito si el backend confirmó. Si falla, el usuario se
      // queda en el formulario con sus respuestas intactas y puede reintentar —
      // antes veía "¡Gracias, te acreditamos 10 imágenes!" aunque no se hubiera
      // guardado ni acreditado nada.
      if (result.status === "error") {
        setSubmitError(result.message);
        return;
      }
      if (result.status === "already_answered") {
        setAlreadyAnswered(true);
        setPhase("success");
        onSubmitted(0); // ya las tenía: 0 imágenes NUEVAS, pero el banner se va
        return;
      }
      setImagesCredited(result.imagesCredited);
      setPhase("success");
      onSubmitted(result.imagesCredited);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Intro screen ──────────────────────────────────────────────────────
  // En mobile (modal full-screen) centramos verticalmente el contenido —
  // el `flex-1` del wrapper crece hasta llenar la altura disponible y el
  // `justify-center` empuja el bloque al centro. En desktop (`sm:block`)
  // se mantiene alineado al top, igual que el resto de los modales del
  // proyecto.
  const introContent = (
    <div className="flex-1 overflow-y-auto flex flex-col justify-center sm:block">
      <div className="px-6 pt-2 pb-4">
        <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4 mx-auto sm:mx-0">
          <Sparkles className="w-5 h-5 text-brand-primary" />
        </div>
        <h2 className="text-[19px] sm:text-[21px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
          Nos encantaría conocer tu opinión
        </h2>
        <p className="text-[15px] text-[#3C3C43]/70 leading-relaxed text-center sm:text-left">
          En Estudio Atlas estamos a tu servicio, por lo que quisiéramos saber
          qué pensás de la plataforma para mejorarla todavía más.
        </p>
        <p className="text-[15px] text-[#3C3C43]/70 leading-relaxed text-center sm:text-left mt-3">
          Respondé esta breve encuesta y recibí{" "}
          <span className="font-semibold text-brand-primary">
            {FREE_IMAGES_REWARD} imágenes extra gratis
          </span>
          .
        </p>
      </div>
      <div className="px-6 pb-6 pt-2">
        <button
          type="button"
          onClick={() => setPhase("form")}
          className="w-full inline-flex items-center justify-center h-11 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
        >
          Responder
        </button>
      </div>
    </div>
  );

  // ── Form screen (wizard paso-a-paso) ─────────────────────────────────
  // Los pasos se arman acá, en cada render, en vez de un `switch (step)` con
  // índices literales: dos son CONDICIONALES (solo existen si Q3 es "sí"), así
  // que el total es 8 o 6 según la respuesta. Con el array, los progress dots,
  // Atrás/Siguiente y el botón final se derivan de `steps.length` y la condición
  // no queda repetida en cuatro lugares.
  //
  // Obligatoriedad: las preguntas de un toque (tienda, publicidad, probó otra
  // IA), la plataforma concreta y el rol. Las de texto largo quedan opcionales —
  // pedir redacción obligatoria hace abandonar, y abandonar pierde TODAS las
  // respuestas porque nada se guarda hasta el envío final.
  const steps: Array<{ id: string; valid: boolean; content: React.ReactNode }> = [
    {
      id: "tienda",
      // "Otra" sin escribir cuál no es una respuesta: no deja avanzar.
      valid:
        !!tiendaOnline &&
        (tiendaOnline !== OPCION_OTRA || tiendaOnlineOtra.trim().length > 0),
      content: (
        <>
          <p className="text-[15px] font-medium text-brand-black/85 mb-3">
            ¿Qué sistema de tienda online utilizás?
          </p>
          {/* Opción única — lista vertical de botones full-width, un ítem por
              línea (mismo patrón visual que el multi-select del paso
              siguiente, pero sin checkbox). */}
          <div className="space-y-2">
            {TIENDA_OPCIONES.map((opt) => (
              <React.Fragment key={opt.id}>
                <button
                  type="button"
                  onClick={() => elegirTienda(opt.id)}
                  className={opcionBtn(tiendaOnline === opt.id)}
                >
                  <PlatformLogoChip id={opt.id} />
                  {opt.label}
                </button>
                {opt.id === OPCION_OTRA && tiendaOnline === OPCION_OTRA && (
                  <InputOtra
                    value={tiendaOnlineOtra}
                    onChange={setTiendaOnlineOtra}
                    placeholder="¿Cuál?"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      ),
    },
    {
      id: "publicidad",
      valid:
        publicidadSelected.length > 0 &&
        (!publicidadSelected.includes(OPCION_OTRA) ||
          publicidadOtra.trim().length > 0),
      content: (
        <>
          <p className="text-[15px] font-medium text-brand-black/85 mb-1">
            ¿Tu compañía paga por publicidad?
          </p>
          <p className="text-[12.5px] text-[#3C3C43]/60 mb-3">
            Podés marcar más de una.
          </p>
          <div className="space-y-2">
            {PUBLICIDAD_OPCIONES.map((opt) => {
              const selected = publicidadSelected.includes(opt.id);
              return (
                <React.Fragment key={opt.id}>
                  <button
                    type="button"
                    onClick={() => togglePublicidad(opt.id)}
                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md border transition-colors text-left ${
                      selected
                        ? "border-brand-primary bg-brand-primary"
                        : "border-neutral-200 bg-white hover:border-brand-primary/40"
                    }`}
                  >
                    {/* El checkbox se invierte cuando la fila está seleccionada:
                        sobre fondo azul, queda como cuadrado blanco con check
                        azul para que se vea contra el bg. */}
                    <span
                      className={`w-5 h-5 rounded-[6px] inline-flex items-center justify-center shrink-0 border ${
                        selected
                          ? "bg-white border-white text-brand-primary"
                          : "border-neutral-300 bg-white"
                      }`}
                    >
                      {selected && <Check className="w-3.5 h-3.5" />}
                    </span>
                    <PlatformLogoChip id={opt.id} />
                    <span
                      className={`text-[15px] ${
                        selected ? "text-white" : "text-brand-black/85"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </button>
                  {opt.id === OPCION_OTRA && selected && (
                    <InputOtra
                      value={publicidadOtra}
                      onChange={setPublicidadOtra}
                      placeholder="¿Cuál?"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      ),
    },
    {
      id: "otra-ia",
      valid: proboOtraIa !== null,
      content: (
        <>
          <p className="text-[15px] font-medium text-brand-black/85 mb-3">
            ¿Ya probaste otra plataforma de IA?
          </p>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => responderProboOtraIa(true)}
              className={opcionBtn(proboOtraIa === true)}
            >
              Sí, probé otra
            </button>
            <button
              type="button"
              onClick={() => responderProboOtraIa(false)}
              className={opcionBtn(proboOtraIa === false)}
            >
              No, Atlas es la primera
            </button>
          </div>
        </>
      ),
    },
    // Las dos que siguen solo existen en la rama "sí": preguntarle cuál probó a
    // quien dijo que ninguna no tiene respuesta posible.
    ...(proboOtraIa === true
      ? [
          {
            id: "cual-otra-ia",
            valid: cualOtraIa.trim().length > 0,
            content: (
              <>
                <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
                  ¿Cuál probaste?
                </label>
                {/* Texto libre y no una lista cerrada: en IA de imágenes
                    aparecen herramientas nuevas todo el tiempo y la lista
                    envejecería sola, además de sesgar hacia lo listado. */}
                <input
                  type="text"
                  value={cualOtraIa}
                  onChange={(e) => setCualOtraIa(e.target.value)}
                  placeholder="Ej: Canva, ChatGPT, Photoroom"
                  autoFocus
                  className="w-full h-11 px-3 border border-neutral-200 rounded-md text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </>
            ),
          },
          {
            id: "atlas-mejor-que",
            valid: true,
            content: (
              <>
                <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
                  ¿Qué hace Atlas mejor que esa plataforma?
                </label>
                <textarea
                  value={atlasMejorQue}
                  onChange={(e) => setAtlasMejorQue(e.target.value)}
                  placeholder="Lo que te hizo quedarte (opcional)"
                  rows={5}
                  autoFocus
                  className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </>
            ),
          },
        ]
      : []),
    {
      id: "rol",
      valid: rolEmpresa.trim().length > 0,
      content: (
        <>
          <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
            ¿Qué rol cumplís en tu empresa?
          </label>
          {/* Input de una línea, no textarea: la respuesta esperada es corta
              ("dueño", "community manager") y un textarea invita a escribir
              de más. */}
          <input
            type="text"
            value={rolEmpresa}
            onChange={(e) => setRolEmpresa(e.target.value)}
            placeholder="Ej: dueño, marketing, community manager"
            autoFocus
            className="w-full h-11 px-3 border border-neutral-200 rounded-md text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
          />
        </>
      ),
    },
    {
      id: "funcion-deseada",
      valid: true,
      content: (
        <>
          <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
            ¿Qué función te gustaría que añadamos en Atlas?
          </label>
          <textarea
            value={funcionDeseada}
            onChange={(e) => setFuncionDeseada(e.target.value)}
            placeholder="Lo que se te ocurra (opcional)"
            rows={5}
            autoFocus
            className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
          />
        </>
      ),
    },
    {
      id: "mejora-sugerida",
      valid: true,
      content: (
        <>
          <label className="block text-[15px] font-medium text-brand-black/85 mb-3">
            ¿Qué no te convence del todo y querés que mejoremos en Atlas?
          </label>
          <textarea
            value={mejoraSugerida}
            onChange={(e) => setMejoraSugerida(e.target.value)}
            placeholder="Tu feedback nos ayuda a priorizar (opcional)"
            rows={5}
            autoFocus
            className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
          />
        </>
      ),
    },
  ];

  // Guarda por si el índice quedó fuera de rango: los pasos condicionales solo
  // pueden desaparecer estando parado en el que los habilita (el de "otra IA",
  // que va antes), así que en la práctica no pasa — pero un índice viejo no
  // puede romper el render.
  const stepIndex = Math.min(step, steps.length - 1);
  const currentStep = steps[stepIndex];
  const isStepValid = currentStep.valid;
  const isLastStep = stepIndex === steps.length - 1;
  const advance = () => {
    if (!isStepValid) return;
    if (isLastStep) {
      handleSubmit();
    } else {
      setStep(stepIndex + 1);
    }
  };
  const goBack = () => {
    if (stepIndex > 0) setStep(stepIndex - 1);
  };

  const formContent = (
    <>
      {/* Body de la pregunta. Los progress dots viven en la top-bar global
          alineados con la X (ver más abajo) — acá solo va el contenido.
          En mobile centramos verticalmente para que con la pantalla full
          la pregunta no quede pegada arriba; en desktop se mantiene
          alineado al top porque el modal tiene altura limitada. Si el
          contenido excede la altura disponible (textareas largos, etc.),
          el `overflow-y-auto` patea adentro de un wrapper flex-shrink-0
          y `min-h-min` evita que el justify-center recorte el inicio. */}
      <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-4 pt-3 flex flex-col justify-center sm:block">
        <div className="w-full">
          {currentStep.content}
          {submitError && (
            <div className="mt-4 rounded-md bg-red-50 border border-red-100 px-3 py-2.5 text-[13.5px] text-red-700">
              {submitError}
            </div>
          )}
        </div>
      </div>
      <div className="px-5 sm:px-6 py-3 border-t border-black/[0.06] shrink-0 flex items-center gap-2">
        <button
          type="button"
          onClick={goBack}
          disabled={stepIndex === 0 || submitting}
          className={`inline-flex items-center justify-center gap-1 h-11 px-4 rounded-md text-[15px] font-medium transition-colors ${
            stepIndex === 0 || submitting
              ? "text-neutral-300 cursor-not-allowed"
              : "text-brand-black/70 hover:bg-neutral-100"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          Atrás
        </button>
        <button
          type="button"
          onClick={advance}
          disabled={!isStepValid || submitting}
          className={`flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md text-[15px] font-medium shadow-sm transition-colors ${
            isStepValid && !submitting
              ? "bg-brand-primary hover:bg-brand-primary-hover text-white"
              : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
          }`}
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando…
            </>
          ) : isLastStep ? (
            `Enviar y obtener ${FREE_IMAGES_REWARD} imágenes`
          ) : (
            <>
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </>
  );

  // ── Success screen ─────────────────────────────────────────────────────
  // Mismo patrón de centrado vertical que intro/form: en mobile el modal
  // es full-screen y el contenido queda centrado; en desktop alineado al
  // top como el resto de los modales.
  const successContent = (
    <div className="flex-1 overflow-y-auto flex flex-col justify-center sm:block">
      <div className="px-6 py-8 text-center">
        <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-success" />
        </div>
        <h2 className="text-[19px] font-semibold text-brand-black/90 mb-1.5">
          {alreadyAnswered ? "Ya habías respondido" : "¡Gracias por tu opinión!"}
        </h2>
        {/* El número sale de la respuesta del backend, no de una constante del
            front: es lo que REALMENTE se acreditó. Y si la empresa ya había
            respondido no prometemos imágenes nuevas — ya las tenía. */}
        <p className="text-[15px] text-[#3C3C43]/70 mb-5">
          {alreadyAnswered ? (
            "Esta encuesta ya estaba respondida, así que las imágenes extra ya están en tu cuenta."
          ) : (
            <>
              Te acreditamos{" "}
              <span className="font-semibold text-brand-primary">
                {imagesCredited} imágenes extra
              </span>
              .
            </>
          )}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center h-11 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
        >
          Listo
        </button>
      </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-stretch sm:items-center justify-center bg-black/50 backdrop-blur-[2px] p-0 sm:p-4"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-[14px] shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar — durante el form muestra los progress dots a la
            izquierda alineados con la X. En intro/success solo la X.
            Los dots indican el paso activo (pill), completados (puntos
            llenos chicos) y pendientes (puntos vacíos chicos). Son tantos
            como pasos REALES: al contestar "sí" en la de otra plataforma
            aparecen dos más, y ese salto es preferible a mostrar de entrada
            dos huecos que el que contesta "no" nunca visita. */}
        <div className="flex items-center justify-between px-3 pt-3 shrink-0 gap-3">
          {phase === "form" ? (
            <div className="flex items-center gap-1.5 pl-2">
              {steps.map((s, i) => {
                const isActive = i === stepIndex;
                const isCompleted = i < stepIndex;
                return (
                  <div
                    key={s.id}
                    className={`h-1.5 rounded-full transition-all ${
                      isActive
                        ? "w-8 bg-brand-primary"
                        : isCompleted
                          ? "w-1.5 bg-brand-primary"
                          : "w-1.5 bg-neutral-200"
                    }`}
                  />
                );
              })}
            </div>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={() => {
              if (!submitting) onClose();
            }}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors shrink-0"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {phase === "intro" && introContent}
        {phase === "form" && formContent}
        {phase === "success" && successContent}
      </div>
    </div>,
    document.body,
  );
};

export default CuestionarioModal;
